import {
  defenceBudget,
  emptyLayout,
  idx,
  type AttackPlan,
  type Coord,
  type Layout,
  type Tile,
  type TileKind,
  type TowerKind,
  type UnitKind,
} from "@empire/core-sim";
import type { Brush } from "../store/editor.js";

/**
 * The first minute (S6-1, 03 §5; cut to three steps on 29 Sep 2026 — see
 * TUTORIAL below).
 *
 * The goal, in the document's words: a stranger understands both sides of the
 * battle, wins something, loses something, sees fog of war once, and only then
 * receives a village.
 *
 *   T1   watch a defence hold              — you win, and learn what towers do
 *   T2   build one yourself, in four beats — you learn the editor by using it
 *   T3   attack a real maze and fail       — you lose, and meet fog
 *
 * T2 used to be a single step that said "draw a road, then put towers beside
 * it" over an empty board with no tools on screen and no sign of where the gate
 * was. It asked for three separate skills at once and offered none of them, so
 * it now hands over one tool at a time and waits until each has been used.
 *
 * Layouts are DATA, per 03 §5 ("exact coordinates are in the backlog as data,
 * not code"), and a test asserts each one is legal and each scripted battle
 * ends the way the script says. A tutorial whose first battle silently stopped
 * teaching what it claims would be worse than no tutorial.
 */

export type Beat = "watch" | "build" | "attack";

/**
 * What a build beat is waiting for.
 *
 * Declarative rather than a predicate in the data, so the steps stay readable
 * as a script and the checking stays in one place in the store.
 */
export type Goal = "gate" | "road" | "towers" | "battle";

export interface TutorialStep {
  readonly id: string;
  readonly beat: Beat;
  /** Build beats only: what has to be true before this step is finished. */
  readonly goal?: Goal;
  /** Max 12 words, per 03 §5. Never a page of text. */
  readonly coach: string;
  /** Shown once the battle has resolved, or the step is done. */
  readonly after: string;
  /**
   * Said when the player asks to move on before the goal is met.
   *
   * It exists because the old build step disabled its button and put the reason
   * in a `title` tooltip — which never appears on a phone, so the tutorial went
   * silent exactly when the player was stuck.
   */
  readonly nudge?: string;
  readonly plan: AttackPlan;
  /** The defence. For a build beat this is the starting point, not the answer. */
  readonly layout: Layout;
  /** Tools the tray offers here. One at a time, in the order they are taught. */
  readonly tools?: readonly Brush[];
  /** A tile the board marks, so "tap here" has a here. */
  readonly focus?: Coord;
  /** How many built towers the "towers" goal is waiting for. */
  readonly minTowers?: number;
  /** True on the step that hands the player a fresh board. */
  readonly resets?: boolean;
  /** T2's battle gets exactly one free retry (09 backlog). */
  readonly retries: number;
}

type RoadTile = readonly [number, number, TileKind];
type TowerAt = readonly [number, number, TowerKind];

function build(
  road: readonly RoadTile[],
  towers: readonly TowerAt[],
  gate: readonly [number, number],
  engineersGuildLevel = 4,
  wallLevel = 0
): Layout {
  const base = emptyLayout();
  const tiles: Tile[] = base.tiles.map((t) => ({ kind: t.kind }));
  for (const [x, y, kind] of road) tiles[idx(x, y, base.size)] = { kind };
  for (const [x, y, kind] of towers) {
    tiles[idx(x, y, base.size)] = { kind: "tower_slot", tower: { kind, level: 1 } };
  }
  return { ...base, tiles, gate: { x: gate[0], y: gate[1] }, wallLevel, engineersGuildLevel };
}

const militia = (count: number, offsetTicks = 0): AttackPlan["waves"][number] => ({
  offsetTicks,
  units: Array.from({ length: count }, () => "militia" as UnitKind),
});

/** Where the attackers come in: the gate, already placed, on the left edge. */
const GATE: Coord = { x: 0, y: 3 };

/** An L used by the test to prove the battle is winnable (see T2_REFERENCE). */
const REFERENCE_PATH: readonly (readonly [number, number])[] = [
  [0, 11],
  [1, 11],
  [2, 11],
  [3, 11],
  [4, 11],
  [5, 11],
  [5, 10],
  [5, 9],
  [5, 8],
  [5, 7],
];

/**
 * Six raiders, in two waves. Twelve (the old number) beat the obvious first
 * maze — a short road and two Arrows — 7 of 12 through, so a first-time
 * player's first battle was usually a loss. Six are held by that maze and
 * still get through one badly placed.
 */
const PLAN: AttackPlan = { waves: [militia(3, 0), militia(3, 30)] };

/**
 * The board the tutorial begins from: Wall 0, Guild 4 — three tower types —
 * with the gate already on the road, so the first thing asked is the one
 * thing worth learning: draw a road.
 */
const BOARD: Layout = build([[GATE.x, GATE.y, "road"]], [], [GATE.x, GATE.y]);

/**
 * Three steps, about a minute (playtest, 29 Sep 2026: "the tutorial is way
 * too long — users are not paying attention or skip it").
 *
 * It used to be six battles and beats — watch a defence, place a gate, draw a
 * road, place towers, fight, then attack a fogged maze — and a three-question
 * survey. What went, and why it could go:
 *  - "watch a defence hold" — the village now starts with a real defence to
 *    look at, and the first battle here is the player's own;
 *  - "place a gate" — a tap that teaches nothing; the gate is placed for you;
 *  - "attack a fogged maze" — the village guide's first step is a real attack
 *    on the Barbarian Outpost, with the real planner, a minute later.
 * What stays is the part only a tutorial can teach: drawing a maze with a
 * finger and seeing it hold.
 */
export const TUTORIAL: readonly TutorialStep[] = [
  {
    id: "road",
    beat: "build",
    goal: "road",
    coach: "Drag a road from the gold gate to the castle.",
    after: "Good. A longer road keeps them under fire longer.",
    nudge: "Keep dragging: the road has to reach the castle.",
    tools: ["road", "erase"],
    focus: GATE,
    layout: BOARD,
    plan: PLAN,
    resets: true,
    retries: 0,
  },
  {
    id: "towers",
    beat: "build",
    goal: "towers",
    coach: "Tap two squares beside the road to build towers.",
    after: "Towers shoot whatever walks past them.",
    nudge: "Tap an empty square right next to the road.",
    // No "Slot" tool: it painted over whatever was under the finger, road
    // included, and broke the maze (friends test, Sep 2026). Towers build
    // their own spot, and only beside the road. An Arrow tower is armed
    // for you, so the first tap builds.
    tools: ["select", "erase"],
    minTowers: 2,
    layout: BOARD,
    plan: PLAN,
    retries: 0,
  },
  {
    id: "battle",
    beat: "build",
    goal: "battle",
    coach: "Six raiders are coming. Send them in.",
    after: "Held! Now build your village.",
    tools: ["road", "select", "erase"],
    layout: BOARD,
    plan: PLAN,
    // One free retry, per the backlog. A first maze that fails is the lesson.
    retries: 1,
  },
];

/** How many road tiles this board still allows — the one number a builder needs. */
export function roadAllowance(layout: Layout): { used: number; allowed: number } {
  const used = layout.tiles.filter(
    (t) => t.kind === "road" || t.kind === "mud" || t.kind === "barricade"
  ).length;
  return { used, allowed: defenceBudget(layout.wallLevel).roadTiles };
}

/** A maze that passes T2, used by the test to prove the step is winnable. */
export const T2_REFERENCE: Layout = build(
  REFERENCE_PATH.map(([x, y]) => [x, y, "road"] as RoadTile),
  [
    [1, 10, "arrow"],
    [4, 10, "arrow"],
    [6, 10, "arrow"],
    [4, 9, "brazier"],
  ],
  [0, 11]
);
